/**
 * pwaUpdater.js — registers /service-worker.js and surfaces an
 * "Update available · Tap to reload" toast whenever a new SW version is
 * detected sitting in the `waiting` state.
 *
 * Flow:
 *   1. register the SW.
 *   2. if there's already a worker waiting on first load (rare but possible
 *      after an aborted update), show the toast immediately.
 *   3. listen for `updatefound` → new worker installs → wait for state
 *      `installed` while another controller exists → show the toast.
 *   4. user taps "Reload" → postMessage('SKIP_WAITING') to the waiting SW.
 *   5. SW activates → fires controllerchange → reload the page once.
 *   6. periodic 60-min poll via `reg.update()` so long-lived sessions
 *      eventually pick up new deploys without a manual refresh.
 *
 * Idempotent: re-importing or re-running this module won't double-register
 * because the browser de-dupes SW registrations by scope.
 */
import { toast } from 'sonner';

let toastShown = false;
let reloadInFlight = false;

function showUpdateToast(waitingWorker) {
  if (toastShown) return;
  toastShown = true;

  toast('Update available', {
    id: 'pwa-update',
    description: 'A new version of Stockaris is ready. Tap to reload.',
    duration: Infinity,
    action: {
      label: 'Reload',
      onClick: () => {
        if (reloadInFlight) return;
        reloadInFlight = true;
        try {
          waitingWorker.postMessage('SKIP_WAITING');
        } catch (_) {
          // If postMessage fails for any reason (no waiting worker, edge race),
          // fall back to a plain location reload — still gets the user to the
          // new version on next request.
          window.location.reload();
        }
      },
    },
  });
}

// Dev/QA-only: expose the toast trigger on window so the screenshot tool
// (and humans poking via DevTools) can validate the visual rendering without
// having to actually deploy a new SW version. The fake worker has a no-op
// postMessage so clicking Reload won't try to skip-wait a non-existent SW.
// Safe to ship — purely a UI demo path with zero side-effects.
if (typeof window !== 'undefined') {
  window.__pwaShowUpdateToastForTest = () => showUpdateToast({ postMessage: () => {} });
}

function trackInstallingWorker(reg) {
  const installing = reg.installing;
  if (!installing) return;
  installing.addEventListener('statechange', () => {
    if (installing.state === 'installed' && navigator.serviceWorker.controller) {
      // A controller already exists → this is an update, not a first install.
      showUpdateToast(installing);
    }
  });
}

export function registerPwaUpdater() {
  if (!('serviceWorker' in navigator)) return;
  if (window.location.protocol !== 'https:') return; // skip dev / http

  window.addEventListener('load', async () => {
    try {
      const reg = await navigator.serviceWorker.register('/service-worker.js');

      // (1) already-waiting case — surface the toast right away.
      if (reg.waiting && navigator.serviceWorker.controller) {
        showUpdateToast(reg.waiting);
      }

      // (2) future updates — fired when reg.update() finds a new SW file.
      reg.addEventListener('updatefound', () => trackInstallingWorker(reg));

      // (3) on activation of a new SW (after user taps Reload), reload the
      // page exactly once so it picks up the new bundle.
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (reloadInFlight) {
          window.location.reload();
        }
      });

      // (4) long-lived sessions — gently nudge SW to check for updates so
      // users who never close the tab still see the toast within an hour.
      setInterval(() => {
        reg.update().catch(() => { /* ignore offline/network blips */ });
      }, 60 * 60 * 1000);
    } catch (_) {
      // PWA is progressive — registration failure must not crash the app.
    }
  });
}
